import { InfoWithLabel } from "@/core/components/info-with-label";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityPricebookItem } from "@/activity-feed/query";
import { formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { ISpiff, ServiceTitanInvoiceItemTrigger } from "@/spiffs/spiffs.d";

interface PricebookItemTriggerDetailsProps {
  spiff: ISpiff;
}

export const PricebookItemTriggerDetails = ({
  spiff,
}: PricebookItemTriggerDetailsProps) => {
  const pricebookItemTrigger = spiff.trigger as ServiceTitanInvoiceItemTrigger;
  const { data: pricebookItem, isLoading } = useGetActivityPricebookItem(
    pricebookItemTrigger.id,
  );

  if (isLoading) {
    return (
      <Stack gap="sm" fullWidth>
        <InfoWithLabel label="Item">
          <Skeleton className="h-5 w-56" />
        </InfoWithLabel>
        <Stack direction="row" gap="none" fullWidth>
          <InfoWithLabel label="Job Completion">
            <Skeleton className="h-5 w-32" />
          </InfoWithLabel>
          <InfoWithLabel label="Invoice Line Item">
            <Skeleton className="h-5 w-24" />
          </InfoWithLabel>
        </Stack>
      </Stack>
    );
  }

  if (!pricebookItem) {
    return (
      <InfoWithLabel label="Pricebook Item">
        <Typography variant="bodyW1" textColor="secondary">
          Pricebook item information unavailable
        </Typography>
      </InfoWithLabel>
    );
  }

  return (
    <Stack gap="sm" fullWidth>
      <InfoWithLabel label="Item">{pricebookItem.display_name}</InfoWithLabel>

      {pricebookItem.service_titan_job?.external_id && (
        <InfoWithLabel label="Associated Job">
          <ServiceTitanJobLink
            jobId={pricebookItem.service_titan_job.external_id}
          />
        </InfoWithLabel>
      )}
      <Stack direction="row" gap="none" fullWidth>
        {pricebookItem.service_titan_job?.completed_at && (
          <InfoWithLabel label="Job Completion">
            {formatDate(pricebookItem.service_titan_job.completed_at)}
          </InfoWithLabel>
        )}
        <InfoWithLabel label="Invoice Line Item">
          {formatMoneyCents(pricebookItem.total_cents)}
        </InfoWithLabel>
      </Stack>
    </Stack>
  );
};
